import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, UntypedFormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { startWith, takeUntil } from 'rxjs/operators';
import {
  NanoSecondsPerDay,
  NanoSecondsPerHour,
  NanoSecondsPerMinute,
  NanoSecondsPerYear,
} from '../../common-action-panel-component.directive';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { GalaxyInputModule } from '@vendasta/galaxy/input';

@Component({
  selector: 'automata-delay-control',
  templateUrl: './delay-control.component.html',
  styleUrls: ['./delay-control.component.scss'],
  imports: [
    CommonModule,
    TranslateModule,
    GalaxyAlertModule,
    MatButtonModule,
    MatCardModule,
    FormsModule,
    GalaxyFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    GalaxyInputModule,
  ],
})
export class DelayControlComponent implements OnInit, OnDestroy {
  @Input() form: UntypedFormGroup;
  @Input() waitLabelTranslationKey: string;
  @Input() sleep: number;
  @Input() disabled: boolean;

  TimeUnit = TimeUnit;
  delayForm: FormGroup;
  destroyed$$ = new Subject<void>();

  constructor(private formBuilder: FormBuilder) {}

  ngOnInit(): void {
    // Determine if the user specified hours or days, defaulting to hours.
    const nanos = this.sleep || 0;
    const timeUnit = getLargestTimeUnit(nanos);
    const timeToDisplay = toTimeUnit(nanos, timeUnit) || 1;

    this.delayForm = this.formBuilder.group(
      {
        sleep: [
          { value: timeToDisplay, disabled: this.disabled },
          [Validators.required, Validators.max(999), Validators.min(1)],
        ],
        timeUnit: [{ value: timeUnit, disabled: this.disabled }, Validators.required],
      },
      {
        validators: [lessThanAYearValidator(), lessThanOne()],
      },
    );

    this.form.addControl('delay', this.delayForm);

    const sleep = this.delayForm.get('sleep');
    const sleep$ = sleep.valueChanges.pipe(startWith(timeToDisplay));

    // Enforce sleep time 0 < sleep < 999
    sleep$.pipe(takeUntil(this.destroyed$$)).subscribe((c) => {
      if (c > 999) {
        sleep.setValue(999, { emitEvent: false });
      }
    });
  }

  ngOnDestroy(): void {
    this.destroyed$$.next();
    this.destroyed$$.complete();
  }
}

function lessThanAYearValidator() {
  return function (form) {
    const sleep = form.get('sleep').value;
    const timeUnit = form.get('timeUnit').value;
    if (convertTimeUnit(sleep, timeUnit, TimeUnit.Nano) > NanoSecondsPerYear) {
      return { overAYear: true };
    }
    return null;
  };
}

function lessThanOne() {
  return function (form) {
    const sleep = form.get('sleep').value;
    const timeUnit = form.get('timeUnit').value;
    if (convertTimeUnit(sleep, timeUnit, TimeUnit.Nano) < NanoSecondsPerMinute) {
      return { lessThanOne: true };
    }
    return null;
  };
}

export enum TimeUnit {
  Nano,
  Minute,
  Hour,
  Day,
}

/**
 * Converts the given nanosecond amount into an amount in the given time unit.
 */
export function toTimeUnit(nanos: number, unit: TimeUnit): number {
  switch (unit) {
    case TimeUnit.Nano:
      return nanos;
    case TimeUnit.Minute:
      return nanos / NanoSecondsPerMinute;
    case TimeUnit.Hour:
      return nanos / NanoSecondsPerHour;
    case TimeUnit.Day:
      return nanos / NanoSecondsPerDay;
    // Default to an hour, rounding if necessary.
    default:
      return Math.floor(nanos / NanoSecondsPerHour);
  }
}

/**
 * Converts the given time in unit 'from' into time in unit 'to'.
 * @param time
 * @param from
 * @param to
 */
export function convertTimeUnit(time: number, from: TimeUnit, to: TimeUnit): number {
  let nanos: number;
  switch (from) {
    case TimeUnit.Nano:
      nanos = time;
      break;
    case TimeUnit.Minute:
      nanos = time * NanoSecondsPerMinute;
      break;
    case TimeUnit.Hour:
      nanos = time * NanoSecondsPerHour;
      break;
    case TimeUnit.Day:
      nanos = time * NanoSecondsPerDay;
      break;

    // Assume hours if unknown.
    default:
      nanos = time * NanoSecondsPerHour;
  }
  return toTimeUnit(nanos, to);
}

/**
 * Returns the largest time unit that by which the nanoseconds can be divided, e.g. day.
 * @param nanos
 */
export function getLargestTimeUnit(nanos: number): TimeUnit {
  if (nanos % NanoSecondsPerDay === 0) {
    return TimeUnit.Day;
  }
  if (nanos % NanoSecondsPerHour === 0) {
    return TimeUnit.Hour;
  }
  if (nanos % NanoSecondsPerMinute === 0) {
    return TimeUnit.Minute;
  }

  // Default to hour.
  return TimeUnit.Hour;
}
